import type { ChangeLogEntry } from "./change-log.model";
import { PreconditionFailedError } from "./errors";
import { CatalogAvailabilityError, CatalogRevisionError, InvalidCatalogDefinitionError } from "./errors.model";
import { type ParsedCanonical, parseCanonicalRef, type VersionedCanonical } from "./lib/canonical";
import type { MemberId } from "./member-identity";
import type { PractitionerId } from "./practitioner.model";

export type { ParsedCanonical, VersionedCanonical } from "./lib/canonical";
// Pinning is not reinvented here: `lib/canonical.ts` already owns the
// `url|version` grammar every materialized record cites, and two spellings of
// one string format is how a pin starts round-tripping wrong.
export { parseCanonicalRef, parsePinnedCanonical, toCanonicalRef } from "./lib/canonical";

// ============================================================
// The spine — what every Catalog Definition carries
//
// A LINEAGE is one definition's permanent identity. A REVISION is one
// immutable version of its content. Each revision persists as its own resource
// sharing the lineage's `url` with a distinct integer `version`; revising
// creates an instance, it never updates one in place.
//
// THE URL IS THE IDENTITY. There is no separate lineage id. It is minted once
// and never derived from the name, so renaming cannot break a reference.
//
// TWO STORAGE GROUPS, and the split is the whole design:
//
//   url · version · mintedAt      revision-level, immutable on each instance
//   name · description · availability
//                                 lineage-level, stored on the CURRENT
//                                 instance, always read from there, corrected
//                                 in place, never minting a revision
//
// Consequences taken deliberately: viewing revision 3 shows today's name and
// the lineage's current availability; name uniqueness is checked against
// current names only, so a name freed by a rename is immediately available;
// and deactivating is one write, not a rewrite of every historical instance.
//
// NOT INCLUDED: `title` — with the url no longer derived from the name,
// nothing machine-facing was left for `name` to be, and two human labels with
// no rule between them is a field nobody knows which to fill. `publisher` —
// no consumer.
//
// AVAILABILITY AND VERSIONING ARE ORTHOGONAL. Availability says whether a
// definition may be used; versioning says what it is. Neither drives the
// other: an inactive definition may be revised and stays inactive afterwards,
// and changing availability never mints a revision because it changes no
// content.
// ============================================================

/**
 * The members, not just the type, because a codec reading a stored code has to
 * check it against the closed set. Derived the other way round — a hand-written
 * array `satisfies` the union — the array could be short a member and nothing
 * would say so, and the codec would then reject a legally stored row.
 */
export const AVAILABILITIES = ["Active", "Inactive"] as const;
export type Availability = (typeof AVAILABILITIES)[number];

/** The revision-level half of the spine. Immutable on the instance that carries it. */
interface RevisionIdentity {
  /** The lineage identity. Minted at creation, never derived from the name, never edited. */
  readonly url: string;
  /**
   * The domain's revision, a positive integer. NOT Medplum's `meta.versionId`,
   * which is a UUID counting technical writes on one instance. Compared
   * numerically, never as a string: plain string sort places "9" above "10",
   * which silently picks the wrong current revision from revision 10 onward.
   */
  readonly version: number;
  /**
   * Stamped at creation and never touched afterwards. As-of reconstruction
   * resolves against this, not `meta.lastUpdated` — a correction moves
   * `lastUpdated` and would make an old revision appear minted on the
   * correction date.
   */
  readonly mintedAt: string;
}

/** The lineage-level half. Lives on the current instance and is corrected in place. */
interface LineageAttributes {
  name: string;
  description?: string;
  availability: Availability;
}

export type CatalogSpine = RevisionIdentity & LineageAttributes;

/** What a correction may touch. Presentation only — anything substantive lives on the revision instance. */
interface SpineCorrection {
  name?: string;
  description?: string;
}

/**
 * Whether the name is claimed. A fact about OTHER lineages, so the caller
 * answers it and the domain decides what the answer means — an aggregate that
 * could see its siblings would be an aggregate with no boundary.
 */
export interface NameAvailabilityFacts {
  nameTaken: boolean;
}

/**
 * Whether every lineage this definition references is currently active.
 * Supplied on activation only: activation is the direction that creates a
 * promise, so it is the direction that is checked.
 */
export interface ActivationFacts {
  /** Urls of referenced lineages that are inactive. Empty means the activation may proceed. */
  inactiveReferences: readonly string[];
}

/** Cross-aggregate availability facts for a command that introduces catalog references. */
export interface ReferenceAvailabilityFacts {
  /** Referenced lineage urls that are inactive. Empty means the proposed references are usable. */
  inactiveReferences: readonly string[];
}

/**
 * A pinned definition selected to create new patient-specific work, carrying the
 * two cross-aggregate facts that decide whether it may be selected at all.
 *
 * `isCurrent` is a fact about SIBLING INSTANCES — whether this revision is the
 * latest of its lineage — so the caller resolves it and the domain decides what
 * the answer means, the same shape as `nameTaken` and `versionTaken`. It cannot
 * be derived from the pin: a revision does not know how many came after it.
 */
export type MaterializableDefinition = VersionedCanonical & {
  availability: Availability;
  isCurrent: boolean;
};

export interface RevisionFacts {
  mintedAt: string;
  /** The version the author loaded before beginning this revision. */
  expectedVersion: number;
  /** Whether another resource already claims the version this command would mint. */
  versionTaken: boolean;
}

export interface CatalogCorrectionChange {
  kind: "CatalogCorrected";
  field: "name" | "description";
  from?: string;
  to?: string;
}

/**
 * One reason a definition is not ready to produce work. Returned, never
 * thrown: two callers act on the same list and reach different decisions — a
 * handler rejects before commit, materialization decides for itself.
 */
export interface CompletenessViolation {
  code: string;
  message: string;
  memberId?: MemberId;
  collection?: string;
}

/**
 * The mechanics every Catalog Definition adopts unchanged. Says nothing about
 * clinical content — the set of definitions is model-specific and this holds
 * regardless of how it lands.
 */
export abstract class CatalogDefinition<Self extends CatalogDefinition<Self>> {
  protected spine: CatalogSpine;
  private pendingCorrections: CatalogCorrectionChange[] = [];
  private revisionBaseline?: string;

  protected constructor(spine: CatalogSpine) {
    assertSpine(spine);
    this.spine = { ...spine };
  }

  /**
   * A name is claimed once across a catalog. Whether it is taken is a fact
   * about OTHER aggregates, so the caller answers it and this decides what the
   * answer means. Static because the definition being created does not exist
   * yet; a correction asks the same question through `correct`.
   */
  static assertNameAvailable(name: string, noun: string, facts: { taken: boolean }): void {
    if (facts.taken) {
      throw new PreconditionFailedError(`A ${noun} named '${name}' already exists`);
    }
  }

  get url(): string {
    return this.spine.url;
  }

  get version(): number {
    return this.spine.version;
  }

  get mintedAt(): string {
    return this.spine.mintedAt;
  }

  get name(): string {
    return this.spine.name;
  }

  get description(): string | undefined {
    return this.spine.description;
  }

  get availability(): Availability {
    return this.spine.availability;
  }

  get isActive(): boolean {
    return this.spine.availability === "Active";
  }

  /** What a materialized record pins: `url|version`, the exact revision it was realized from. */
  get pin(): VersionedCanonical {
    return { url: this.spine.url, version: String(this.spine.version) };
  }

  /** Whether `pin` names exactly this revision. */
  isPinnedBy(pin: VersionedCanonical): boolean {
    const own = this.pin;
    return pin.url === own.url && pin.version === own.version;
  }

  /** A copy. The spine is state, and handing out the live object would let a caller edit around the commands. */
  get spineFacts(): CatalogSpine {
    return { ...this.spine };
  }

  /**
   * Fix presentation. Lineage-level fields only, mutating in place, minting no
   * revision — fixing a typo is not a new version of the clinical work.
   *
   * The revision/correction split is enforced by WHICH OBJECT THE WRITE
   * TARGETS, not by classifying intent: a substantive change cannot be filed
   * as a correction because the field does not live on the object this
   * touches. That also removes any incentive to misfile a change in order to
   * avoid minting a revision.
   */
  correct(changes: SpineCorrection, facts: NameAvailabilityFacts): void {
    if (changes.name !== undefined) {
      assertName(changes.name);
      if (changes.name !== this.spine.name && facts.nameTaken) {
        throw new InvalidCatalogDefinitionError(`A definition named '${changes.name}' already exists`, {
          url: this.spine.url,
          name: changes.name,
        });
      }
      if (changes.name !== this.spine.name) {
        const from = this.spine.name;
        this.spine.name = changes.name;
        this.pendingCorrections.push({ kind: "CatalogCorrected", field: "name", from, to: changes.name });
      }
    }
    if ("description" in changes && changes.description !== this.spine.description) {
      const from = this.spine.description;
      this.spine.description = changes.description;
      this.pendingCorrections.push({
        kind: "CatalogCorrected",
        field: "description",
        ...(from === undefined ? {} : { from }),
        ...(changes.description === undefined ? {} : { to: changes.description }),
      });
    }
  }

  /**
   * Produce the next member of the lineage: same `url`, `version + 1`, fresh
   * `mintedAt`, availability carried across.
   *
   * A REVISION IS NOT A MUTATION. It is a different resource, so this returns
   * a new aggregate rather than changing the receiver. The mutation happens
   * afterwards, on the returned instance, inside the session:
   *
   *     const next = protocol.revise({ mintedAt, expectedVersion, versionTaken })
   *     next.setWork(work, workFacts)
   *     session.add(next)
   *     await session.commit()      // one transaction bundle
   *
   * The mutable draft is the loaded aggregate inside a session. That is
   * in-memory transactional state ending at `commit()`, not the `draft`
   * lifecycle status the three collapsing vocabularies deleted.
   *
   * `revise` is on the entity rather than in a handler because the entity is
   * the only thing that knows which changes require one. In a handler, every
   * caller re-implements "is this substantive?".
   *
   * Store-dependent guards and the mint timestamp are required facts. The
   * entity decides their meaning while the caller resolves them, so stale or
   * colliding revisions cannot bypass the behavior.
   *
   * Cost: `revise` cannot be composed with other mutations in one call. That
   * is correct rather than limiting — correcting and revising produce
   * different numbers of resources, so they cannot be one act.
   */
  revise(facts: RevisionFacts): Self {
    if (this.spine.version !== facts.expectedVersion) {
      throw new CatalogRevisionError(
        `Expected revision ${facts.expectedVersion} but the current revision is ${this.spine.version}`,
        { url: this.spine.url, version: this.spine.version },
      );
    }
    const nextVersion = this.spine.version + 1;
    if (facts.versionTaken) {
      throw new CatalogRevisionError(`Revision ${nextVersion} of ${this.spine.url} already exists`, {
        url: this.spine.url,
        version: nextVersion,
      });
    }
    if (!facts.mintedAt) {
      throw new CatalogRevisionError("A revision requires the date it was minted", {
        url: this.spine.url,
        version: nextVersion,
      });
    }
    const next = this.copyForRevision({ ...this.spine, version: nextVersion, mintedAt: facts.mintedAt });
    next.revisionBaseline = this.substantiveSnapshot();
    return next;
  }

  /**
   * Reconstruct the concrete aggregate through its normal validation boundary.
   * Every subclass names the state that survives; runtime object layout is not
   * an implicit cloning contract.
   */
  protected abstract copyForRevision(spine: CatalogSpine): Self;

  /** Clinical content only. Identity high-water marks and persistence state do not make a revision substantive. */
  protected abstract substantiveFacts(): unknown;

  /** Called by the session before persistence; an unchanged draft is not a revision. */
  assertCommittable(): void {
    if (this.revisionBaseline !== undefined && this.revisionBaseline === this.substantiveSnapshot()) {
      throw new CatalogRevisionError("Revision does not change the definition", {
        url: this.spine.url,
        version: this.spine.version,
      });
    }
  }

  /**
   * Put the lineage back in circulation. Checked, because activation is the
   * direction that creates a promise: a treatment requiring a deactivated
   * device kind would otherwise build protocols that can never be booked. The
   * referenced definitions have to be loaded by the handler and passed in —
   * the entity cannot reach across its own boundary to find them.
   */
  activate(facts: ActivationFacts): void {
    if (this.spine.availability === "Active") {
      throw new CatalogAvailabilityError(`'${this.spine.name}' is already active`, { url: this.spine.url });
    }
    if (facts.inactiveReferences.length > 0) {
      throw new CatalogAvailabilityError(
        `'${this.spine.name}' cannot be activated while it references inactive definitions: ${facts.inactiveReferences.join(", ")}`,
        { url: this.spine.url },
      );
    }
    this.spine.availability = "Active";
  }

  /**
   * Withdraw the lineage. NEVER BLOCKED, whatever references it — blocking
   * would gate a safety act behind revising every dependent protocol first,
   * which is exactly the wrong order under a recall. The disclosure of what
   * breaks is a confirm step in the UI, shown before this is called.
   *
   * Its own command rather than `correct({ availability })`: a correction
   * fixes something that was wrong, deactivation is a decision about the world.
   */
  deactivate(): void {
    if (this.spine.availability === "Inactive") {
      throw new CatalogAvailabilityError(`'${this.spine.name}' is already inactive`, { url: this.spine.url });
    }
    this.spine.availability = "Inactive";
  }

  /**
   * Why this definition is not ready to produce work. PURE, RETURNS A LIST,
   * THROWS NOTHING — a definition that is structurally valid can still be
   * incomplete, and the two callers of that answer act on it differently.
   *
   * Models needing facts they cannot reach (a protocol needs the definitions
   * its occurrences cite) expose a free function taking those facts instead: a
   * method that only works if you hand it the world is not the aggregate
   * answering for itself.
   */
  completenessViolations(): readonly CompletenessViolation[] {
    return [];
  }

  /**
   * An active definition may not acquire an inactive dependency. Hydration is
   * deliberately exempt: a dependency can be deactivated after this revision
   * was written, and old state must remain readable.
   */
  protected assertReferencesActive(facts: ReferenceAvailabilityFacts): void {
    if (this.isActive && facts.inactiveReferences.length > 0) {
      throw new CatalogAvailabilityError(
        `'${this.spine.name}' cannot reference inactive definitions: ${facts.inactiveReferences.join(", ")}`,
        { url: this.spine.url },
      );
    }
  }

  hasPendingChanges(): boolean {
    return this.pendingCorrections.length > 0;
  }

  drainChangeLog(context: {
    at: string;
    by: PractitionerId;
    reason?: string;
  }): readonly ChangeLogEntry<CatalogCorrectionChange>[] {
    if (this.pendingCorrections.length === 0) return [];
    const changes = this.pendingCorrections;
    this.pendingCorrections = [];
    return [{ at: context.at, by: context.by, reason: context.reason, changes }];
  }

  private substantiveSnapshot(): string {
    return JSON.stringify(this.substantiveFacts(), (_key, value) =>
      value && typeof value === "object" && !Array.isArray(value)
        ? Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)))
        : value,
    );
  }
}

/**
 * A definition may only be selected for new work, or materialized, while it is
 * active. A PINNED reference to an inactive definition still resolves —
 * existing work owns its frozen configuration and is unaffected.
 */
export function assertSelectable(definition: { isActive: boolean; name: string; url: string }): void {
  if (!definition.isActive) {
    throw new CatalogAvailabilityError(`'${definition.name}' is inactive and cannot be used for new work`, {
      url: definition.url,
    });
  }
}

/**
 * New patient-specific work may pin only the CURRENT revision of an ACTIVE
 * lineage. Two separate refusals, because the caller's remedy differs: an
 * inactive lineage is reactivated or abandoned, a superseded revision is
 * reloaded.
 *
 * Returns the BARE PIN. The facts gated the choice and are not part of what was
 * chosen — copying them onto the record would be a stale second answer to a
 * question the catalog already owns.
 *
 * ONLY THE CHOOSING IS GATED, never loading. Hydration paths take a
 * `VersionedCanonical` directly, so work pinned to a lineage deactivated or
 * revised afterwards stays readable — which is the whole reason a pin resolves
 * independently of availability.
 */
export function materializeDefinition(definition: MaterializableDefinition): VersionedCanonical {
  if (definition.availability !== "Active") {
    throw new CatalogAvailabilityError(`'${definition.url}' is inactive and cannot produce new work`, {
      url: definition.url,
    });
  }
  if (!definition.isCurrent) {
    throw new CatalogRevisionError(
      `Revision ${definition.version} of '${definition.url}' has been superseded and cannot produce new work`,
      { url: definition.url, version: Number(definition.version) },
    );
  }
  return { url: definition.url, version: definition.version };
}

/** Names reach human surfaces and uniqueness checks, so a blank one is refused at the boundary. */
export function assertName(name: string): void {
  if (name.trim() === "") {
    throw new InvalidCatalogDefinitionError("A definition must have a name");
  }
}

function assertSpine(spine: CatalogSpine): void {
  if (!spine.url || spine.url.trim() === "") {
    throw new InvalidCatalogDefinitionError("A definition must have a canonical url");
  }
  if (!Number.isInteger(spine.version) || spine.version < 1) {
    throw new CatalogRevisionError(`Revision must be a positive whole number, got ${spine.version}`, {
      url: spine.url,
    });
  }
  if (!spine.mintedAt) {
    throw new InvalidCatalogDefinitionError("A revision must carry the date it was minted", { url: spine.url });
  }
  assertName(spine.name);
}

/** The initial revision of every lineage. */
const INITIAL_REVISION = 1;

/**
 * The spine of a brand new lineage. The first successful save creates revision
 * `1` and makes the lineage `Active` — save IS approval, there is no review
 * step and no persisted draft.
 */
export function initialSpine(input: {
  url: string;
  mintedAt: string;
  name: string;
  description?: string;
}): CatalogSpine {
  return {
    url: input.url,
    version: INITIAL_REVISION,
    mintedAt: input.mintedAt,
    name: input.name,
    description: input.description,
    availability: "Active",
  };
}

/**
 * A definition-to-definition reference. Bare canonical url, NEVER `|version`:
 * references inside the catalog identify a LINEAGE and always resolve to its
 * current revision. Pins exist only on the output side, on the work produced.
 */
export function assertUnpinnedReference(ref: string): string {
  const parsed: ParsedCanonical | undefined = parseCanonicalRef(ref);
  if (!parsed) {
    throw new InvalidCatalogDefinitionError("A definition reference must not be empty");
  }
  if (parsed.version) {
    throw new InvalidCatalogDefinitionError(
      `Catalog references identify a lineage, not a revision: '${ref}' is version-pinned`,
      { url: parsed.url },
    );
  }
  return parsed.url;
}
